import { useVerbiageQuery } from "@repo/baseCloud";
import { MvcBranch, MvcLocation } from "@repo/models";
import { filterPositionsWithinSquare, Marker } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { FC, useMemo } from "react";

import { useLocationApi } from "../../api/locationApi";
import * as queries from "../../api/queries";
import { useLocationContext } from "../../contexts/editableItemContext";
import { defaultPinImage, mapObjToMarker, PinStyle } from "./markers";
import { PinMap, usePinFilter } from "./pinMap";

enum PinTypes {
	ThisLocation,
	SamePartnerType,
	SamePartner,
	OtherLocation,
	ThisOffice,
	SameVendor,
	OtherOffice,
}

const pinStyles: Record<PinTypes, PinStyle> = {
	[PinTypes.ThisLocation]: { color: "#ef4444", image: defaultPinImage, ordinal: 1, scale: 2 },
	[PinTypes.SamePartner]: { color: "#ef4444", image: defaultPinImage, ordinal: 2, scale: 1 },
	[PinTypes.SamePartnerType]: { color: "#3b82f6", image: defaultPinImage, ordinal: 3, scale: 1 },
	[PinTypes.OtherLocation]: { color: "#22c55e", image: defaultPinImage, ordinal: 4, scale: 1 },
	[PinTypes.ThisOffice]: { color: "#ec4899", image: defaultPinImage, ordinal: 1, scale: 2 },
	[PinTypes.SameVendor]: { color: "#eab308", image: defaultPinImage, ordinal: 2, scale: 1 },
	[PinTypes.OtherOffice]: { color: "#771177", image: defaultPinImage, ordinal: 3, scale: 1 },
};

interface MappedLocation extends MvcLocation {
	id: number;
	code: string;
	pinType: PinTypes;
}

interface MappedBranch extends MvcBranch {
	id: number;
	code: string;
	pinType: PinTypes;
}

function mapLocationToMarker(location: MappedLocation): Marker {
	return mapObjToMarker(location, pinStyles[location.pinType]);
}

function mapBranchToMarker(branch: MappedBranch): Marker {
	const marker = mapObjToMarker(branch, pinStyles[branch.pinType]);
	return {
		...marker,
		label: [{ ...marker.label[0], Vendor: branch.marketer }],
	};
}

function getLocationPinType(location: MvcLocation, other: MvcLocation) {
	if (location.locationId === other.locationId) return PinTypes.ThisLocation;
	if (other.partnerId === location.partnerId) return PinTypes.SamePartner;
	if (other.partnerTypeId === location.partnerTypeId) return PinTypes.SamePartnerType;
	return PinTypes.OtherLocation;
}

function getOfficePinType(location: MvcLocation, other: MvcBranch) {
	if (location.officeNumber.includes(other.officeNumber)) return PinTypes.ThisOffice;
	if (location.marketerName === other.marketer) return PinTypes.SameVendor;
	return PinTypes.OtherOffice;
}

export const Map: FC = () => {
	const { getAllLocations, getAllBranches } = useLocationApi();
	const { item: location } = useLocationContext();
	const { verbiage } = useVerbiageQuery();
	const filter = usePinFilter<PinTypes>([
		PinTypes.ThisLocation,
		PinTypes.SamePartner,
		PinTypes.SamePartnerType,
		PinTypes.ThisOffice,
		PinTypes.SameVendor,
	]);

	const locationsQuery = useQuery({ queryKey: [queries.GROUP.ALL_LOCATIONS], queryFn: getAllLocations });
	const branchesQuery = useQuery({ queryKey: [queries.ALL_BRANCHES], queryFn: getAllBranches });

	// These memos keep `markers` stable: MapChart re-places every pin and refits the bounds when it changes
	const locations = useMemo<MappedLocation[]>(() => {
		const nearby = filterPositionsWithinSquare(location.lat, location.lon, locationsQuery.data ?? [], 100);
		return nearby.map((l) => ({
			...l,
			id: l.locationId,
			code: l.locationCode,
			pinType: getLocationPinType(location, l),
		}));
	}, [locationsQuery.data, location]);

	const branches = useMemo<MappedBranch[]>(() => {
		const mapped =
			branchesQuery.data?.map((b) => ({
				...b,
				id: b.branchId,
				code: b.officeNumber,
				pinType: getOfficePinType(location, b),
			})) ?? [];

		const nearby = filterPositionsWithinSquare(location.lat, location.lon, mapped, 100);
		const thisOffice = mapped.filter((b) => b.pinType === PinTypes.ThisOffice);

		return [...nearby, ...thisOffice];
	}, [branchesQuery.data, location]);

	const positions = useMemo(() => {
		return [
			...locations.filter((pos) => filter.isActive(pos.pinType)).map(mapLocationToMarker),
			...branches.filter((pos) => filter.isActive(pos.pinType)).map(mapBranchToMarker),
		];
	}, [locations, branches, filter]);

	return (
		<PinMap
			id="location-map"
			loading={!location || locationsQuery.isLoading}
			markers={positions}
			pinStyles={pinStyles}
			filter={filter}
			legend={[
				{ pinType: PinTypes.SamePartner, text: location.partnerName ?? "Same Partner" },
				{ pinType: PinTypes.SamePartnerType, text: location.partnerTypeName ?? "Same Partner Type" },
				{ pinType: PinTypes.OtherLocation, text: "Other " + verbiage.location },
				{ pinType: PinTypes.ThisOffice, text: `${verbiage.location} ${verbiage.office}` },
				{ pinType: PinTypes.SameVendor, text: `Other ${location.marketerName} ${verbiage.office}` },
				{ pinType: PinTypes.OtherOffice, text: "Other " + verbiage.office },
			]}
		/>
	);
};
